import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, finalize, interval, map, Observable, Subscription, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { EesztAuthMethod, EesztAuthState } from '../types/eeszt-auth';
import {
  EesztSamlEidRequest,
  EesztSamlEidResponse,
  EesztSamlEszigRequest,
  EesztSamlMobilRequest,
  EesztSamlOtpRequest,
  EesztSamlTokenResponse,
  EszigAuthResponse,
  OtpFormValue
} from '../types/eeszt';
import { Auth } from './auth';

@Injectable({
  providedIn: 'root'
})
export class EesztAuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly ESZIG_READER_URL = environment.eszigReaderUrl;
  private readonly STORAGE_KEY = 'eesztTokenExpiration';
  private readonly STORAGE_METHOD_KEY = 'eesztAuthMethod';

  private http = inject(HttpClient);
  private toastr = inject(ToastrService);
  private authService = inject(Auth);
  private destroyRef = inject(DestroyRef);

  private timerSubscription?: Subscription;

  // State signals
  tokenExpiration = signal<Date | null>(null);
  authMethod = signal<EesztAuthMethod | null>(null);
  remainingSeconds = signal<number>(0);

  // Loading states
  loading = signal({
    eid: false,
    mobile: false,
    otp: false
  });

  // Computed signals
  isAuthenticated = computed(() => {
    const exp = this.tokenExpiration();
    return exp !== null && exp.getTime() > Date.now();
  });

  authState = computed((): EesztAuthState => ({
    isAuthenticated: this.isAuthenticated(),
    expirationDate: this.tokenExpiration(),
    remainingSeconds: this.remainingSeconds(),
    authMethod: this.authMethod()
  }));

  constructor() {
    this.loadStoredToken();
  }

  authenticateWithEid(): Observable<void> {
    const user = this.authService.currentUser();
    if (!user) {
      this.toastr.error('Felhasználó nem található');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, eid: true }));

    const request: EesztSamlEidRequest = {
      // QUESTION: use eesztUserId instead of id ???
      userId: user.id,
      servicePointId: user.servicePoint?.id ?? 0,
      callerTransactionId: crypto.randomUUID(),
      hisNonce: crypto.randomUUID()
    };

    return this.http.post<EesztSamlEidResponse>(
      `${this.API_URL}/rest/eeszt/saml/require-token/eid`,
      request
    ).pipe(
      switchMap(response => this.eszigAuth(response.data)),
      switchMap(eszigResponse => {
        const eszigRequest: EesztSamlEszigRequest = {
          // QUESTION: use eesztUserId instead of id ???
          userId: user.id,
          servicePointId: user.servicePoint?.id ?? 0,
          eidToken: eszigResponse.id_token
        };
        return this.http.post<EesztSamlTokenResponse>(
          `${this.API_URL}/rest/eeszt/saml/get-token/eszig`,
          eszigRequest
        );
      }),
      tap(response => {
        this.persistToken(response.expirationDate, 'eid');
        this.toastr.success('Sikeres authentikálás!');
      }),
      map(() => void 0),
      catchError(error => {
        console.error('eID authentication error:', error);
        this.toastr.error('Hiba történt az eID authentikáció során');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, eid: false })))
    );
  }

  authenticateWithMobile(motp: string): Observable<void> {
    const user = this.authService.currentUser();
    if (!user) {
      this.toastr.error('Felhasználó nem található');
      return EMPTY;
    }

    if (!motp?.trim()) {
      this.toastr.error('Kérem adja meg a mobil tokent');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, mobile: true }));

    const request: EesztSamlMobilRequest = {
      motp: motp.trim(),
      userId: user.eesztUserId,
      servicePointId: user.servicePoint?.id ?? 0
    };

    return this.http.post<EesztSamlTokenResponse>(
      `${this.API_URL}/rest/eeszt/saml/get-token/mobil`,
      request
    ).pipe(
      tap(response => {
        this.persistToken(response.expirationDate, 'mobile');
        this.toastr.success('Sikeres authentikálás!');
      }),
      map(() => void 0),
      catchError(error => {
        console.error('Mobile authentication error:', error);
        this.toastr.error('Hiba történt a mobil authentikáció során');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, mobile: false })))
    );
  }

  authenticateWithOtp(credentials: OtpFormValue): Observable<void> {
    const user = this.authService.currentUser();
    if (!user) {
      this.toastr.error('Felhasználó nem található');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, otp: true }));

    const request: EesztSamlOtpRequest = {
      otp: credentials.otp,
      password: credentials.password,
      username: credentials.userName,
      servicePointId: user.servicePoint?.id ?? 0
    };

    return this.http.post<EesztSamlTokenResponse>(
      `${this.API_URL}/rest/eeszt/saml/get-token/otp`,
      request
    ).pipe(
      tap(response => {
        this.persistToken(response.expirationDate, 'otp');
        this.toastr.success('Sikeres authentikálás!');
      }),
      map(() => void 0),
      catchError(error => {
        console.error('OTP authentication error:', error);
        this.toastr.error('Hiba történt az OTP authentikáció során');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, otp: false })))
    );
  }

  private eszigAuth(token: string): Observable<EszigAuthResponse> {
    return this.http.get<EszigAuthResponse>(`${this.ESZIG_READER_URL}${token}`);
  }

  private persistToken(expirationDate: string, method: EesztAuthMethod): void {
    const expDate = new Date(expirationDate);
    localStorage.setItem(this.STORAGE_KEY, expDate.getTime().toString());
    localStorage.setItem(this.STORAGE_METHOD_KEY, method);
    this.tokenExpiration.set(expDate);
    this.authMethod.set(method);
    this.startExpirationTimer();
  }

  loadStoredToken(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    const storedMethod = localStorage.getItem(this.STORAGE_METHOD_KEY) as EesztAuthMethod | null;

    if (stored) {
      const expDate = new Date(parseInt(stored, 10));
      if (expDate.getTime() > Date.now()) {
        this.tokenExpiration.set(expDate);
        this.authMethod.set(storedMethod);
        this.startExpirationTimer();
      } else {
        this.clearToken();
      }
    }
  }

  clearToken(): void {
    this.timerSubscription?.unsubscribe();
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_METHOD_KEY);
    this.tokenExpiration.set(null);
    this.authMethod.set(null);
    this.remainingSeconds.set(0);
  }

  private startExpirationTimer(): void {
    this.timerSubscription?.unsubscribe();

    this.timerSubscription = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const exp = this.tokenExpiration();
        if (exp) {
          const remaining = Math.floor((exp.getTime() - Date.now()) / 1000);
          if (remaining <= 0) {
            this.clearToken();
            this.toastr.warning('EESZT autentikáció lejárt');
          } else {
            this.remainingSeconds.set(remaining);
          }
        }
      });
  }
}
